import { GlobalSearchParams } from 'store/api/globalSearch';
import { GlobalSearchFacetDimension, GlobalSearchUpdatedBucket, isGlobalSearchEntityType } from 'types/globalSearch';

/** The query itself, which is the one parameter the page cannot be read without. */
export const QUERY_PARAM = 'q';

/** The facet dimensions in the order the rail draws them. */
export const FACET_DIMENSIONS: GlobalSearchFacetDimension[] = [
    'ENTITY_TYPE',
    'OFFICE',
    'RESPONSIBLE',
    'PRACTICE_AREA',
    'UPDATED',
];

/**
 * Which URL parameter carries each dimension. They are the endpoint's own parameter names, repeated
 * once per selected value exactly as it binds them, so the page's whole state is the query string —
 * which is what makes a narrowed result list shareable and reload-safe.
 */
export const FACET_PARAM: Record<GlobalSearchFacetDimension, string> = {
    ENTITY_TYPE: 'entityType',
    OFFICE: 'officeId',
    RESPONSIBLE: 'responsibleId',
    PRACTICE_AREA: 'practiceAreaId',
    UPDATED: 'updated',
};

const UPDATED_BUCKETS: GlobalSearchUpdatedBucket[] = ['TODAY', 'THIS_WEEK', 'THIS_MONTH', 'THIS_YEAR', 'OLDER'];

/** Everything the rail narrows by, in the shape the search hook takes it. */
export type SearchFacetFilters = Pick<
    GlobalSearchParams,
    'entityType' | 'officeId' | 'responsibleId' | 'practiceAreaId' | 'updated'
>;

/** Whether a raw URL or facet value names a record family this build knows. */
export const isEntityType = isGlobalSearchEntityType;

function recordIds(params: URLSearchParams, name: string): number[] {
    return params
        .getAll(name)
        .map(Number)
        .filter((id) => Number.isSafeInteger(id) && id > 0);
}

/**
 * The selections a results URL carries.
 *
 * Anything that is not a value the backend knows is dropped rather than forwarded: a hand-edited or
 * stale link would otherwise turn into a 400 on a page whose whole job is to answer.
 */
export function parseSearchFilters(params: URLSearchParams): SearchFacetFilters {
    return {
        entityType: params.getAll(FACET_PARAM.ENTITY_TYPE).filter(isEntityType),
        officeId: recordIds(params, FACET_PARAM.OFFICE),
        responsibleId: recordIds(params, FACET_PARAM.RESPONSIBLE),
        practiceAreaId: recordIds(params, FACET_PARAM.PRACTICE_AREA),
        updated: params
            .getAll(FACET_PARAM.UPDATED)
            .filter((value): value is GlobalSearchUpdatedBucket =>
                (UPDATED_BUCKETS as readonly string[]).includes(value)
            ),
    };
}

/** What is currently selected in one dimension, as the raw values the facets are keyed by. */
export function selectedFacetValues(params: URLSearchParams, dimension: GlobalSearchFacetDimension): string[] {
    return params.getAll(FACET_PARAM[dimension]);
}

/** Whether anything at all is narrowing the list, which is what makes "clear all" worth offering. */
export function hasFacetSelection(params: URLSearchParams): boolean {
    return FACET_DIMENSIONS.some((dimension) => params.has(FACET_PARAM[dimension]));
}

/** The same URL with one facet value switched on or off, every other selection left alone. */
export function toggleFacetValue(
    params: URLSearchParams,
    dimension: GlobalSearchFacetDimension,
    value: string
): URLSearchParams {
    const name = FACET_PARAM[dimension];
    const next = new URLSearchParams(params);
    const kept = params.getAll(name).filter((current) => current !== value);
    next.delete(name);
    for (const current of kept) {
        next.append(name, current);
    }
    if (kept.length === params.getAll(name).length) {
        next.append(name, value);
    }
    return next;
}

/** The same URL narrowed by nothing. The query survives; it is not a filter. */
export function clearFacetValues(params: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(params);
    for (const dimension of FACET_DIMENSIONS) {
        next.delete(FACET_PARAM[dimension]);
    }
    return next;
}
